import IconAdultOnly from '@/components/assets/icon_adult_only.svg'
import IconUserNumber from '@/components/assets/icon_gamepad.svg'
import IconKey from '@/components/assets/icon_key.svg'
import Loader from '@/components/Loader'
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import ViverseInputOTP from '@/components/ViverseInputOTP'
import ViverseSwitch from '@/components/ViverseSwitch'
import { MAX_CONCURRENT_USERS, WorldSettingsFormValues } from '@/hooks/useWorldSettingsFormSchema'
import { cn } from '@/lib/utils'
import { motion } from 'framer-motion'
import { useTranslations } from 'next-intl'
import { FC } from 'react'
import { useFormContext } from 'react-hook-form'

export type WorldSettingPermissionsTabProps = {
  policy: string
  enablePasscode: boolean
  concurrentUsers: number
  handlePasscodeSwitchChange: (checked: boolean) => void
  isLoadingRoomSettings: boolean
  externalData: any
}

const WorldSettingPermissionsTab: FC<WorldSettingPermissionsTabProps> = ({
  policy,
  enablePasscode,
  concurrentUsers,
  handlePasscodeSwitchChange,
  isLoadingRoomSettings,
  externalData,
}) => {
  const t = useTranslations()
  const form = useFormContext<WorldSettingsFormValues>()

  return (
    <div
      className={cn(
        policy !== 'public' && 'opacity-30',
        'flex h-full w-full flex-1 flex-col gap-2 md:gap-4',
      )}
    >
      <p className="md:subheader-1 subheader-2">
        {t('profile-page.my-worlds-card-settings-entry')}
      </p>
      <FormField
        control={form.control}
        name="passcode"
        render={({ field }) => (
          <FormItem className={'w-full p-2 md:p-4'}>
            <FormLabel className="mb-0 flex w-full flex-row items-center justify-between gap-4">
              <FormLabel className="flex flex-row items-center gap-6">
                <IconKey className="fill-vive-primary-20 h-5 w-5 shrink-0 md:h-6 md:w-6" />
                <div>
                  <p className="text-vive-primary-20 body-1 text-[14px] md:text-[16px]">
                    {t('profile-page.my-worlds-card-settings-passcode')}
                  </p>
                  <p className="text-vive-primary-40 body-2 text-[12px] md:text-[14px]">
                    {t('profile-page.my-worlds-card-settings-passcode-tip')}
                  </p>
                </div>
              </FormLabel>
              <FormField
                control={form.control}
                name="enablePasscode"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex flex-col"></FormLabel>
                    <FormControl>
                      <ViverseSwitch
                        checked={field.value}
                        onCheckedChange={handlePasscodeSwitchChange}
                        disabled={policy !== 'public'}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </FormLabel>
            <FormControl className="mt-2">
              <div className={enablePasscode && policy === 'public' ? '' : 'hidden'}>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: enablePasscode ? 1 : 0 }}
                  transition={{ ease: 'easeOut' }}
                >
                  <div className="ml-12 flex items-center">
                    <ViverseInputOTP
                      maxLength={6}
                      {...field}
                      hasError={!!form.formState.errors.passcode}
                    />
                  </div>
                </motion.div>
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {!isLoadingRoomSettings && !externalData?.is_standalone && (
        <FormField
          control={form.control}
          name="concurrentUsers"
          render={({ field }) => (
            <FormItem className="flex w-full flex-row items-center gap-4 p-2 md:p-4">
              <FormLabel className="mb-0 flex flex-1 flex-row items-center gap-6">
                <IconUserNumber className="fmd:w-6 fill-vive-primary-20 h-5 w-5 shrink-0 md:h-6" />
                <p className="text-vive-primary-20 body-1 text-[14px] md:text-[16px]">
                  {t('spaces-create-modal.concurrent-users')}
                </p>
              </FormLabel>
              <Select
                key={`select-concurrent-users-${concurrentUsers}`}
                onValueChange={(value) => field.onChange(Number(value))}
                defaultValue={field.value?.toString()}
                value={field.value?.toString()}
              >
                <FormControl>
                  <SelectTrigger
                    className="bg-vive-primary-70/50 hover:border-vive-brand-50 [&>span]:text-vive-primary-20 [&>span]:body-2 h-[34px] w-[110px] rounded-lg border-none font-medium transition-all md:h-14 md:w-[196px]"
                    disabled={policy !== 'public'}
                  >
                    {isLoadingRoomSettings ? <Loader className="size-3" /> : <SelectValue />}
                  </SelectTrigger>
                </FormControl>
                <SelectContent className="bg-vive-primary-70 max-w-[calc(100%-4px)] [&>div]:flex [&>div]:flex-col [&>div]:gap-1">
                  {Array(MAX_CONCURRENT_USERS)
                    .fill(null)
                    .map((_, index) => {
                      const value = index + 1
                      return (
                        <SelectItem
                          key={`select-item-${value}`}
                          value={value?.toString()}
                          className={cn(
                            'focus:hover:bg-vive-primary-70 [&>span]:text-vive-primary-20 hover:focus:[&>span]:text-vive-white w-full max-w-[calc(100%-4px)] cursor-pointer transition-all duration-75',
                            {
                              'bg-vive-primary-70': concurrentUsers === value,
                            },
                          )}
                        >
                          {value}
                        </SelectItem>
                      )
                    })}
                </SelectContent>
              </Select>
            </FormItem>
          )}
        />
      )}
      <FormField
        control={form.control}
        name="isAdultOnly"
        render={({ field }) => (
          <FormItem className="flex w-full flex-row items-center justify-between gap-4 p-2 md:p-4">
            <FormLabel className="mb-0 flex flex-row items-center gap-6">
              <IconAdultOnly className="fill-vive-primary-20 h-5 w-5 shrink-0 md:h-6 md:w-6" />
              <div>
                <p className="text-vive-primary-20 body-1 text-[14px] md:text-[16px]">
                  {t('profile-page.my-worlds-card-settings-age-verification')}
                </p>
                <p className="text-vive-primary-40 body-2 text-[12px] md:text-[14px]">
                  {t('profile-page.my-worlds-card-settings-age-verification-desc')}
                </p>
              </div>
            </FormLabel>
            <FormControl>
              <ViverseSwitch
                checked={field.value}
                onCheckedChange={field.onChange}
                disabled={policy !== 'public'}
              />
            </FormControl>
          </FormItem>
        )}
      />
      {/* 共同擁有者區塊 */}
      {/* <FormField
        control={form.control}
        name="coOwners"
        render={() => (
          <FormItem>
            <FormLabel>Co-owners</FormLabel>
            <p className="text-vive-primary-40 mb-4">
              You can set up to five co-owners to share full editing and host privileges when
              visiting this World or viewing live streams.
            </p>
            <FormControl>
              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-2">
                  <div className="text-sm font-medium">Name</div>
                  <ViverseInput
                    placeholder="Search user ID or display name"
                    onChange={(e) => {
                      setKeyword(e.target.value)
                    }}
                  />
                  {showResult && (
                    <div className="flex flex-col gap-2">
                      {searchResult.map((result) => (
                        <div key={result.id}>{result.display_name}</div>
                      ))}
                    </div>
                  )}
                  <div
                    className={
                      selected.length > 0 ? 'mt-3 flex flex-wrap gap-2' : 'flex flex-wrap gap-2'
                    }
                  >
                    {tempCoOwnerList.map((option) => (
                      <Button
                        className="bg-vive-primary-70 text-vive-primary-20 hover:text-vive-primary-10 gap-2 rounded-full"
                        key={option.id}
                        onClick={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                        }}
                      >
                        {option.display_name}
                        <CircleX
                          onClick={() => handleRemoveCoOwner(option.id)}
                          fill="#D5E2F2"
                          stroke="#364356"
                        />
                      </Button>
                    ))}
                  </div>
                </div>
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      /> */}
    </div>
  )
}

export default WorldSettingPermissionsTab
